import { readLandingJourneyId } from '@/lib/landingJourney';
import { webStorageUtil } from '@/state/webStorage.state.service';

import {
  EPortalRumEvents,
  logPortalRumError,
  logPortalRumInfo,
} from './coralogix';

export enum EPortalJourneyFlow {
  PORTAL_BOOT = 'portal-boot',
  LOGIN = 'login',
  OTP_ROUND_TRIP = 'otp-round-trip',
  STORE_REDIRECT = 'store-redirect',
  EMBEDDED_STORE = 'embedded-store',
  CHECKOUT = 'checkout',
  SUPPORT = 'support',
  LOGOUT = 'logout',
  SESSION = 'session',
}

enum EPortalJourneyPhase {
  STARTED = 'started',
  COMPLETED = 'completed',
  FAILED = 'failed',
  ABANDONED = 'abandoned',
}

interface IPortalJourneyContext extends Record<string, unknown> {
  authMethod?: string;
  destinationHost?: string;
  loadingArea?: string;
  pathname?: string;
  reasonCode?: string;
  source?: string;
  statusCode?: number;
}

interface ICreatePortalJourneyProps {
  flow: EPortalJourneyFlow;
  context?: IPortalJourneyContext;
  createJourneyId?: () => string;
  now?: () => number;
}

interface IPortalJourney {
  journeyId: string;
  complete: (context?: IPortalJourneyContext) => void;
  fail: (context?: IPortalJourneyContext) => void;
  abandon: (context?: IPortalJourneyContext) => void;
  markStep: (step: string, context?: IPortalJourneyContext) => void;
}

interface IStoredPortalJourney {
  context: IPortalJourneyContext;
  journeyId: string;
  startedAtMilliseconds: number;
}

interface IUnloadAbandonableJourney {
  abandon: (context?: IPortalJourneyContext) => void;
}

export interface IStoreRedirectJourneySnapshot {
  destinationHost: string;
  journeyId: string;
  pathname: string;
  startedAtMilliseconds: number;
}

const SAFE_CONTEXT_KEYS = [
  'authMethod',
  'destinationHost',
  'loadingArea',
  'pathname',
  'reasonCode',
  'source',
  'statusCode',
] as const;

const unloadAbandonableJourneys = new Map<string, IUnloadAbandonableJourney>();

const createDefaultJourneyId = (): string =>
  readLandingJourneyId() || crypto.randomUUID();
const getJourneyStorageKey = (flow: EPortalJourneyFlow): string =>
  `ac_portal_journey_${flow}`;

const registerForUnloadAbandon = (
  journeyId: string,
  journey: IUnloadAbandonableJourney
): void => {
  unloadAbandonableJourneys.set(journeyId, journey);
};

const unregisterForUnloadAbandon = (journeyId: string): void => {
  unloadAbandonableJourneys.delete(journeyId);
};

export const abandonInFlightPortalJourneys = (
  context: IPortalJourneyContext = {}
): void => {
  unloadAbandonableJourneys.forEach((journey) => {
    journey.abandon(context);
  });
  unloadAbandonableJourneys.clear();
};

export const createPortalJourney = ({
  flow,
  context = {},
  createJourneyId = createDefaultJourneyId,
  now = Date.now,
}: ICreatePortalJourneyProps): IPortalJourney => {
  const journeyId = createJourneyId();
  const startedAtMilliseconds = now();
  const baseData = createJourneyData(flow, journeyId, context);
  let isFinished = false;

  logPortalRumInfo(EPortalRumEvents.PORTAL_JOURNEY_STARTED, {
    ...baseData,
    phase: EPortalJourneyPhase.STARTED,
  });

  const finish = (
    phase: Exclude<EPortalJourneyPhase, EPortalJourneyPhase.STARTED>,
    terminalContext: IPortalJourneyContext = {}
  ): void => {
    if (isFinished) return;
    isFinished = true;
    unregisterForUnloadAbandon(journeyId);

    const eventData = {
      ...baseData,
      ...pickSafeContext(terminalContext),
      durationMilliseconds: now() - startedAtMilliseconds,
      phase,
    };

    if (phase === EPortalJourneyPhase.COMPLETED) {
      logPortalRumInfo(EPortalRumEvents.PORTAL_JOURNEY_COMPLETED, eventData);
      return;
    }

    const eventName =
      phase === EPortalJourneyPhase.FAILED
        ? EPortalRumEvents.PORTAL_JOURNEY_FAILED
        : EPortalRumEvents.PORTAL_JOURNEY_ABANDONED;
    logPortalRumError(eventName, eventData);
  };

  registerForUnloadAbandon(journeyId, {
    abandon: (terminalContext): void =>
      finish(EPortalJourneyPhase.ABANDONED, terminalContext),
  });

  return {
    journeyId,
    complete: (terminalContext): void =>
      finish(EPortalJourneyPhase.COMPLETED, terminalContext),
    fail: (terminalContext): void =>
      finish(EPortalJourneyPhase.FAILED, terminalContext),
    abandon: (terminalContext): void =>
      finish(EPortalJourneyPhase.ABANDONED, terminalContext),
    markStep: (step, terminalContext = {}): void => {
      if (isFinished) return;
      logPortalRumInfo(EPortalRumEvents.PORTAL_JOURNEY_STEP_COMPLETED, {
        ...baseData,
        ...pickSafeContext(terminalContext),
        durationMilliseconds: now() - startedAtMilliseconds,
        phase: 'step-completed',
        step,
      });
    },
  };
};

export const startPersistentPortalJourney = ({
  flow,
  context = {},
  createJourneyId = createDefaultJourneyId,
  now = Date.now,
}: ICreatePortalJourneyProps): string => {
  if (readStoredPortalJourney(flow)) {
    abandonPersistentPortalJourney(flow, { reasonCode: 'superseded' });
  }

  const journeyId = createJourneyId();
  const safeContext = pickSafeContext(context);
  const startedAtMilliseconds = now();
  const journey: IStoredPortalJourney = {
    context: safeContext,
    journeyId,
    startedAtMilliseconds,
  };
  persistStoredPortalJourney(flow, journey);
  logPortalRumInfo(EPortalRumEvents.PORTAL_JOURNEY_STARTED, {
    ...createJourneyData(flow, journeyId, safeContext),
    phase: EPortalJourneyPhase.STARTED,
  });
  return journeyId;
};

export const completePersistentPortalJourney = (
  flow: EPortalJourneyFlow,
  context: IPortalJourneyContext = {},
  now: () => number = Date.now
): void => {
  finishPersistentPortalJourney(
    flow,
    EPortalJourneyPhase.COMPLETED,
    context,
    now
  );
};

export const failPersistentPortalJourney = (
  flow: EPortalJourneyFlow,
  context: IPortalJourneyContext = {},
  now: () => number = Date.now
): void => {
  finishPersistentPortalJourney(
    flow,
    EPortalJourneyPhase.FAILED,
    context,
    now
  );
};

export const abandonPersistentPortalJourney = (
  flow: EPortalJourneyFlow,
  context: IPortalJourneyContext = {},
  now: () => number = Date.now
): void => {
  finishPersistentPortalJourney(
    flow,
    EPortalJourneyPhase.ABANDONED,
    context,
    now
  );
};

export const abandonStaleOtpRoundTripJourney = (
  context: IPortalJourneyContext = {},
  now: () => number = Date.now
): void => {
  if (!readStoredPortalJourney(EPortalJourneyFlow.OTP_ROUND_TRIP)) {
    return;
  }

  abandonPersistentPortalJourney(EPortalJourneyFlow.OTP_ROUND_TRIP, context, now);
};

export const registerStoreRedirectJourneyForUnloadAbandon = ({
  destinationHost,
  journeyId,
  pathname,
  startedAtMilliseconds = Date.now(),
}: IStoreRedirectJourneySnapshot): void => {
  const baseData = {
    destinationHost,
    flow: EPortalJourneyFlow.STORE_REDIRECT,
    journeyId,
    pathname,
  };

  registerForUnloadAbandon(journeyId, {
    abandon: (terminalContext): void => {
      logPortalRumError(EPortalRumEvents.PORTAL_JOURNEY_ABANDONED, {
        ...baseData,
        ...pickSafeContext(terminalContext),
        durationMilliseconds: Date.now() - startedAtMilliseconds,
        phase: EPortalJourneyPhase.ABANDONED,
      });
    },
  });
};

export const finishStoreRedirectJourneyForUnloadAbandon = (
  journeyId: string
): void => {
  unregisterForUnloadAbandon(journeyId);
};

const createJourneyData = (
  flow: EPortalJourneyFlow,
  journeyId: string,
  context: IPortalJourneyContext
): Record<string, unknown> => ({
  appSessionId: webStorageUtil.getStorageWithFallback()?.id,
  ...pickSafeContext(context),
  flow,
  journeyId,
});

const pickSafeContext = (
  context: IPortalJourneyContext = {}
): Record<string, unknown> =>
  SAFE_CONTEXT_KEYS.reduce<Record<string, unknown>>((safeContext, key) => {
    if (context[key] !== undefined) {
      safeContext[key] = context[key];
    }
    return safeContext;
  }, {});

const finishPersistentPortalJourney = (
  flow: EPortalJourneyFlow,
  phase:
    | EPortalJourneyPhase.COMPLETED
    | EPortalJourneyPhase.FAILED
    | EPortalJourneyPhase.ABANDONED,
  context: IPortalJourneyContext,
  now: () => number
): void => {
  const storageKey = getJourneyStorageKey(flow);
  const storedValue = readAndRemoveStoredPortalJourney(storageKey);
  if (!storedValue) return;

  try {
    const journey = JSON.parse(storedValue) as IStoredPortalJourney;
    const eventData = {
      ...createJourneyData(flow, journey.journeyId, journey.context),
      ...pickSafeContext(context),
      durationMilliseconds: now() - journey.startedAtMilliseconds,
      phase,
    };
    if (phase === EPortalJourneyPhase.COMPLETED) {
      logPortalRumInfo(EPortalRumEvents.PORTAL_JOURNEY_COMPLETED, eventData);
      return;
    }
    const eventName =
      phase === EPortalJourneyPhase.FAILED
        ? EPortalRumEvents.PORTAL_JOURNEY_FAILED
        : EPortalRumEvents.PORTAL_JOURNEY_ABANDONED;
    logPortalRumError(eventName, eventData);
  } catch {
    return;
  }
};

const readStoredPortalJourney = (
  flow: EPortalJourneyFlow
): IStoredPortalJourney | undefined => {
  const storageKey = getJourneyStorageKey(flow);
  try {
    const storedValue = sessionStorage.getItem(storageKey);
    if (!storedValue) {
      return undefined;
    }
    return JSON.parse(storedValue) as IStoredPortalJourney;
  } catch {
    return undefined;
  }
};

const readAndRemoveStoredPortalJourney = (
  storageKey: string
): string | undefined => {
  try {
    const storedValue = sessionStorage.getItem(storageKey);
    sessionStorage.removeItem(storageKey);
    return storedValue || undefined;
  } catch {
    return undefined;
  }
};

const persistStoredPortalJourney = (
  flow: EPortalJourneyFlow,
  journey: IStoredPortalJourney
): void => {
  try {
    sessionStorage.setItem(
      getJourneyStorageKey(flow),
      JSON.stringify(journey)
    );
  } catch {
    return;
  }
};
